import { whenStudioReady } from 'Studio/whenStudioReady'
import type { ResourcesServiceType } from 'types/studio/helpers'

export type { ResourcesServiceType }

const POLL_INTERVAL_MS = 100
const DEFAULT_TIMEOUT_MS = 30000

/**
 * Returns a Promise that resolves when window.AccountHelper.resourcesService is available.
 * First waits for the design studio to load (AccountHelper/WorkspaceHelper), then resolves
 * with resourcesService. Resolves immediately if already defined; otherwise polls until ready.
 */
export function whenResourceServiceReady(timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<ResourcesServiceType> {
  return whenStudioReady(timeoutMs).then(() => {
    const instance = typeof window !== 'undefined' && window.AccountHelper?.resourcesService
    if (instance) {
      return instance as ResourcesServiceType
    }
    return new Promise<ResourcesServiceType>((resolve, reject) => {
      const deadline = Date.now() + timeoutMs
      const id = window.setInterval(() => {
        const service = typeof window !== 'undefined' && window.AccountHelper?.resourcesService
        if (service) {
          window.clearInterval(id)
          resolve(service as ResourcesServiceType)
          return
        }
        if (Date.now() >= deadline) {
          window.clearInterval(id)
          reject(new Error('useResourceService: resourcesService not available within timeout'))
        }
      }, POLL_INTERVAL_MS)
    })
  })
}

/**
 * Ensures the real instance is ready then delegates. Implements the ResourcesService API;
 * each method awaits whenResourceServiceReady() then delegates. Callers never need to
 * null-check or await "ready" — they just call e.g. await resourcesService.getList(...).
 */
class ResourcesServiceReady implements ResourcesServiceType {
  private instancePromise: Promise<ResourcesServiceType> | null = null

  private getInstance(): Promise<ResourcesServiceType> {
    if (!this.instancePromise) this.instancePromise = whenResourceServiceReady()
    return this.instancePromise
  }

  async getList<T>(arg: {
    resource: string
    includeArchived?: boolean
    includeShared?: boolean
    query?: Record<string, string>
    options?: { skipCaching?: boolean }
  }): Promise<T[] | undefined> {
    const instance = await this.getInstance()
    return instance.getList(arg) as Promise<T[] | undefined>
  }

  async getOne<T>(arg: {
    resource: string
    identifier: string
    value: string | number
    options?: { skipCaching?: boolean }
  }): Promise<T | undefined> {
    const instance = await this.getInstance()
    return instance.getOne(arg) as Promise<T | undefined>
  }

  getResourceCache(resource: string): Promise<unknown | undefined> {
    return whenResourceServiceReady().then((instance) => instance.getResourceCache(resource))
  }

  clearCache(): void {
    whenResourceServiceReady().then((instance) => instance.clearCache())
  }

  async prewarmStudioProfileResources(orgId: number): Promise<unknown[]> {
    const instance = await this.getInstance()
    return instance.prewarmStudioProfileResources(orgId)
  }

  async silentRefreshList<T>(arg: {
    resource: string
    includeArchived?: boolean
    includeShared?: boolean
  }): Promise<T[] | undefined> {
    const instance = await this.getInstance()
    return instance.silentRefreshList(arg) as Promise<T[] | undefined>
  }

  async watchList<T = unknown>(arg: {
    resource: string
    includeArchived?: boolean
    includeShared?: boolean
    onChangeCallback: (data: T[] | undefined) => void
  }): Promise<{ data: T[] | undefined; unsubscribe: () => void }> {
    const instance = await this.getInstance()
    return instance.watchList(arg) as Promise<{ data: T[] | undefined; unsubscribe: () => void }>
  }
}

let resourcesServiceReadySingleton: ResourcesServiceReady | null = null

/**
 * React hook that always returns the same resourcesService (singleton). The returned
 * object is a ResourcesServiceReady wrapper that delegates to the real instance after
 * awaiting design studio load internally. No null check or isReady needed: call e.g.
 * await resourcesService.getList({ resource: 'battery_schemes' }) and it will wait for the
 * real service if needed.
 *
 * @returns { resourcesService: ResourcesServiceType } — singleton, same instance for all callers.
 */
export function useResourceService(): ResourcesServiceType {
  if (resourcesServiceReadySingleton === null) {
    resourcesServiceReadySingleton = new ResourcesServiceReady()
  }
  return resourcesServiceReadySingleton
}
